import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, Users, Globe, Monitor, Smartphone, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';

const FLAG = cc => { if (!cc || cc.length !== 2) return ''; const f = cc.toUpperCase().split('').map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)); return f.join(''); };

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [sections, setSections] = useState(null);
  const [days, setDays] = useState('7');
  const [sectionPage, setSectionPage] = useState('/');

  useEffect(() => {
    adminApi.analyticsOverview({ days: parseInt(days) }).then(r => setData(r.data)).catch(() => {});
  }, [days]);

  useEffect(() => {
    adminApi.analyticsSections({ page: sectionPage, days: parseInt(days) }).then(r => setSections(r.data)).catch(() => {});
  }, [sectionPage, days]);

  const tooltipStyle = { contentStyle: { background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' } };

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Page Analytics</h2>
          <p className="text-muted-foreground mt-1">Visitor tracking and engagement data</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Today</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/50"><CardContent className="p-5">
          <div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Page Views</p><p className="text-3xl font-bold mt-1">{data?.total_views?.toLocaleString() || 0}</p></div><div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center"><Eye className="w-5 h-5 text-primary" /></div></div>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-5">
          <div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Unique Visitors</p><p className="text-3xl font-bold mt-1">{data?.unique_visitors?.toLocaleString() || 0}</p></div><div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center"><Users className="w-5 h-5 text-cyan-400" /></div></div>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-5">
          <div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Online Now</p><p className="text-3xl font-bold mt-1 text-emerald-400">{data?.online_now || 0}</p></div><div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Activity className="w-5 h-5 text-emerald-400" /></div></div>
        </CardContent></Card>
        <Card className="bg-card border-border/50"><CardContent className="p-5">
          <div className="flex items-center justify-between"><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Devices</p><p className="text-lg font-bold mt-1"><Monitor className="w-4 h-4 inline mr-1" />{data?.devices?.desktop || 0} <Smartphone className="w-4 h-4 inline ml-2 mr-1" />{data?.devices?.mobile || 0}</p></div><div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center"><Globe className="w-5 h-5 text-amber-400" /></div></div>
        </CardContent></Card>
      </div>

      {/* Daily chart */}
      {data?.daily?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Daily Page Views</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="views" stroke="#6366f1" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="unique_visitors" stroke="#22d3ee" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Pages */}
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Top Pages</CardTitle></CardHeader>
          <CardContent>
            {(data?.top_pages || []).length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p> : (
              <div className="space-y-1">
                {data.top_pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                      <span className="text-sm font-mono truncate">{p.page}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{p.unique_visitors} unique</span>
                      <Badge variant="outline" className="text-xs font-mono">{p.views}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Countries */}
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-lg">Visitor Countries</CardTitle></CardHeader>
          <CardContent>
            {(data?.countries || []).length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p> : (
              <div className="space-y-1">
                {data.countries.map((c, i) => {
                  const pct = data.unique_visitors > 0 ? Math.round((c.count / data.unique_visitors) * 100) : 0;
                  return (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{FLAG(c.country_code)}</span>
                        <span className="text-sm">{c.country}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{width: `${pct}%`}} /></div>
                        <span className="text-xs font-mono text-muted-foreground w-8 text-right">{pct}%</span>
                        <span className="text-xs font-mono w-8 text-right">{c.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section Engagement */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Section Engagement</CardTitle>
            <Select value={sectionPage} onValueChange={setSectionPage}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(data?.top_pages || []).map(p => <SelectItem key={p.page} value={p.page}>{p.page}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {!sections?.sections?.length ? <p className="text-sm text-muted-foreground py-4 text-center">No section data for this page</p> : (
            <div className="space-y-1">
              {sections.sections.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                  <span className="text-sm font-mono">{s.section}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-muted-foreground">{(s.avg_duration_ms / 1000).toFixed(1)}s avg</span>
                    <Badge variant="outline" className="text-xs font-mono">{s.views} views</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
